import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Plus, Trash2, Eye, Printer, Search, FileDown, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'domestic', label: 'Domestic Sales' },
];

const blankItem = () => ({ description: '', hsn: '', quantity: 1, rate: 0, gst_rate: 5, discount_percent: 0 });
const todayIso = () => new Date().toISOString().slice(0, 10);

const AdminManualInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    invoice_number: '',
    date: todayIso(),
    category: 'gallery',
    party_name: '',
    party_address: '',
    party_gstin: '',
    items: [blankItem()],
    notes: '',
  });

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/manual-invoices`, { withCredentials: true });
      setInvoices(data.invoices || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };

  const computedTotals = useMemo(() => {
    let subtotal = 0, gst = 0;
    form.items.forEach((it) => {
      const discPct = Math.max(0, Math.min(100, Number(it.discount_percent) || 0));
      const discountedRate = (Number(it.rate) || 0) * (1 - discPct / 100);
      const taxable = (Number(it.quantity) || 0) * discountedRate;
      const gstAmt = taxable * (Number(it.gst_rate) || 0) / 100;
      subtotal += taxable; gst += gstAmt;
    });
    return { subtotal: +subtotal.toFixed(2), gst: +gst.toFixed(2), total: +(subtotal + gst).toFixed(2) };
  }, [form.items]);

  const setItem = (idx, key, val) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const resetForm = () => setForm({
    invoice_number: '', date: todayIso(), category: 'gallery',
    party_name: '', party_address: '', party_gstin: '',
    items: [blankItem()], notes: '',
  });

  const submit = async () => {
    if (!form.invoice_number.trim()) { toast.error('Invoice number is required'); return; }
    if (!form.party_name.trim()) { toast.error('Party name is required'); return; }
    if (!form.items.some((it) => it.description.trim())) { toast.error('Add at least one item with a description'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter((it) => it.description.trim()).map((it) => ({
          description: it.description.trim(),
          hsn: (it.hsn || '').trim(),
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          gst_rate: Number(it.gst_rate) || 0,
          discount_percent: Number(it.discount_percent) || 0,
        })),
      };
      await axios.post(`${API}/api/admin/manual-invoices`, payload, { withCredentials: true });
      toast.success('Invoice saved');
      setDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save invoice');
    } finally { setSubmitting(false); }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/manual-invoices/${invoiceId}`, { withCredentials: true });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/api/admin/manual-invoices/${inv.invoice_id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safe = (inv.invoice_number || inv.invoice_id).replace(/[\\/\s]/g, '_');
      link.setAttribute('download', `tax_invoice_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Failed to download PDF');
    }
  };

  const emailPdf = async (inv) => {
    const to = window.prompt('Send tax invoice PDF to (email):', '');
    if (to == null) return;
    if (!to.trim() || !/^\S+@\S+\.\S+$/.test(to.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/api/admin/manual-invoices/${inv.invoice_id}/email`,
        { to_email: to.trim() },
        { withCredentials: true }
      );
      toast.success(data.message || 'Invoice emailed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to email invoice'); }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (filter !== 'all' && inv.category !== filter) return false;
      if (!q) return true;
      return (
        inv.invoice_number.toLowerCase().includes(q) ||
        (inv.party?.name || '').toLowerCase().includes(q) ||
        (inv.party?.gstin || '').toLowerCase().includes(q)
      );
    });
  }, [invoices, filter, search]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => navigate('/admin')} data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="manual-invoices-heading">
              <FileText className="h-7 w-7 text-primary" /> Tax Invoices — Gallery & Domestic
            </h1>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-invoice-button">
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex gap-1 bg-white border border-[#E0E0E0] rounded-full p-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => setFilter(c.value)}
                className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full transition ${filter === c.value ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`}
                data-testid={`filter-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by invoice number, party, GSTIN" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search-input" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="manual-invoices-table">
              <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Party</th>
                  <th className="px-4 py-3">GSTIN</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No invoices yet. Click "New Invoice" to create one.</td></tr>
                ) : rows.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`invoice-row-${inv.invoice_id}`}>
                    <td className="px-4 py-3 font-bold">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(inv.date)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${inv.category === 'gallery' ? 'bg-pink-50 text-pink-800' : 'bg-blue-50 text-blue-800'}`}>
                        {inv.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">{inv.party?.name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{inv.party?.gstin || '—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-primary">{'\u20B9'}{(inv.total || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => navigate(`/admin/manual-invoices/print/${inv.invoice_id}`)} data-testid={`view-invoice-${inv.invoice_id}`}>
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => window.open(`/admin/manual-invoices/print/${inv.invoice_id}?print=1`, '_blank')} data-testid={`print-invoice-${inv.invoice_id}`}>
                          <Printer className="h-3 w-3 mr-1" /> Print
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => downloadPdf(inv)} data-testid={`pdf-invoice-${inv.invoice_id}`}>
                          <FileDown className="h-3 w-3 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => emailPdf(inv)} data-testid={`email-invoice-${inv.invoice_id}`}>
                          <Mail className="h-3 w-3 mr-1" /> Email
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => deleteInvoice(inv.invoice_id)} data-testid={`delete-invoice-${inv.invoice_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-3">Showing {rows.length} of {invoices.length} invoices.</p>

        {/* New Invoice Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Tax Invoice</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase">Category</label>
                <select className="w-full border border-[#E0E0E0] rounded-md px-3 py-2 text-sm bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                  <option value="gallery">Gallery</option>
                  <option value="domestic">Domestic Sales</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase">Invoice Number</label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="e.g. INV-2026-001" data-testid="form-invoice-number" />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase">Date</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-muted-foreground uppercase">Party Name</label>
                <Input value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} placeholder="Customer / business name" data-testid="form-party-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground uppercase">Party GSTIN</label>
                <Input value={form.party_gstin} onChange={(e) => setForm({ ...form, party_gstin: e.target.value })} placeholder="Optional" data-testid="form-party-gstin" />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-bold text-muted-foreground uppercase">Party Address</label>
                <Input value={form.party_address} onChange={(e) => setForm({ ...form, party_address: e.target.value })} placeholder="Full billing address" data-testid="form-party-address" />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold">Items</h3>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-button"><Plus className="h-3 w-3 mr-1" /> Add row</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-[#E0E0E0] rounded-md">
                  <thead className="bg-[#FAFAFA]">
                    <tr className="text-left text-[10px] uppercase font-bold text-muted-foreground">
                      <th className="px-2 py-2">Description</th>
                      <th className="px-2 py-2 w-24">HSN</th>
                      <th className="px-2 py-2 w-20">Qty</th>
                      <th className="px-2 py-2 w-24">Rate</th>
                      <th className="px-2 py-2 w-20">Disc %</th>
                      <th className="px-2 py-2 w-24">GST %</th>
                      <th className="px-2 py-2 w-28 text-right">Amount</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const discPct = Math.max(0, Math.min(100, Number(it.discount_percent) || 0));
                      const discountedRate = (Number(it.rate) || 0) * (1 - discPct / 100);
                      const taxable = (Number(it.quantity) || 0) * discountedRate;
                      const gstAmt = taxable * (Number(it.gst_rate) || 0) / 100;
                      const lineTotal = taxable + gstAmt;
                      return (
                        <tr key={idx} className="border-t border-[#F0F0F0]" data-testid={`form-item-${idx}`}>
                          <td className="px-2 py-1"><Input className="h-8 text-xs" value={it.description} onChange={(e) => setItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} /></td>
                          <td className="px-2 py-1"><Input className="h-8 text-xs" value={it.hsn} onChange={(e) => setItem(idx, 'hsn', e.target.value)} data-testid={`item-hsn-${idx}`} /></td>
                          <td className="px-2 py-1"><Input type="number" min="0" step="0.01" className="h-8 text-xs" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} data-testid={`item-qty-${idx}`} /></td>
                          <td className="px-2 py-1"><Input type="number" min="0" step="0.01" className="h-8 text-xs" value={it.rate} onChange={(e) => setItem(idx, 'rate', e.target.value)} data-testid={`item-rate-${idx}`} /></td>
                          <td className="px-2 py-1"><Input type="number" min="0" max="100" step="0.01" className="h-8 text-xs" value={it.discount_percent ?? 0} onChange={(e) => setItem(idx, 'discount_percent', e.target.value)} data-testid={`item-discount-${idx}`} /></td>
                          <td className="px-2 py-1"><Input type="number" min="0" step="0.01" className="h-8 text-xs" value={it.gst_rate} onChange={(e) => setItem(idx, 'gst_rate', e.target.value)} data-testid={`item-gst-${idx}`} /></td>
                          <td className="px-2 py-1 text-right font-bold whitespace-nowrap">{'\u20B9'}{lineTotal.toFixed(2)}</td>
                          <td className="px-1"><button type="button" className="text-destructive hover:bg-destructive/10 rounded p-1" onClick={() => removeItem(idx)} aria-label="Remove row" data-testid={`item-remove-${idx}`}><Trash2 className="h-3 w-3" /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-6 mt-3 text-sm">
                <div className="text-muted-foreground">Subtotal: <b className="text-foreground">{'\u20B9'}{computedTotals.subtotal.toFixed(2)}</b></div>
                <div className="text-muted-foreground">GST: <b className="text-foreground">{'\u20B9'}{computedTotals.gst.toFixed(2)}</b></div>
                <div className="text-base">Total: <b className="text-primary">{'\u20B9'}{computedTotals.total.toFixed(2)}</b></div>
              </div>
            </div>

            <div className="mt-3">
              <label className="text-xs font-bold text-muted-foreground uppercase">Notes (optional)</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, delivery info, etc." data-testid="form-notes" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting} data-testid="cancel-invoice-button">Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="save-invoice-button">{submitting ? 'Saving…' : 'Save Invoice'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminManualInvoices;
